import { memo, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { LayoutGrid, Map as MapIcon, Maximize2, MessageCircle, Minimize2, Video } from 'lucide-react'
import { IconButton, Tabs, type TabItem } from '@/components/play'
import type { WatchTab } from '@/lib/watch/types'
import './watch.css'

/**
 * What fills a phone on its side: the video, the camera grid or the chat beside the video, or the map (the video in a
 * small player over it).
 */
export type LandscapeView = 'video' | 'cameras' | 'map' | 'chat'

export interface PhoneTabsProps {
  /** The open tab (`usePhoneTab`). */
  tab: WatchTab
  onTabChange(tab: WatchTab): void
  /** Chat messages that arrived while the chat was off screen (`useStageChat().unread`), counted on Chat. */
  unread: number
  /** The panels' contents: the camera grid, the course map and the chat. */
  cameras: ReactNode
  map: ReactNode
  chat: ReactNode
  /** What ends the Cameras panel's scroll upright, after the grid (the site's footer); on its side it stays, hidden. */
  footer?: ReactNode
  /**
   * A phone on its side: the upright switcher and its panels hide (still mounted), and a switcher of its own (Video,
   * Cameras, Map, Chat) picks what fills the screen (`view`).
   */
  landscape?: boolean
  /** Landscape: what fills the screen. */
  view?: LandscapeView
  onViewChange?(view: LandscapeView): void
  /** Upright, on the Map tab: the map fills the screen (the page hides the rest). */
  fullMap?: boolean
  /** The map can fill the screen: the Map tab offers its full-screen toggle. Left out, it doesn't. */
  onFullMapChange?(full: boolean): void
  /**
   * The stage on screen as a button that opens the Stages sheet (the event has other stages): on its side it sits
   * beside the switcher over the video (beside the chat, the chat's head does the same).
   */
  stageButton?: ReactNode
}

/** What a dialog or menu is open for: its own Escape, not the page's. */
const POPUP = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]'

/** The selected tab of a tab list inside `root`. */
const selectedTab = (root: HTMLElement | null) => root?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]') ?? null

/**
 * Focus has nowhere to be: on the page, or left in something now hidden or inert (a closing panel is inert at once and
 * hidden only once its exit is over, and a picked tile's neighbour can take focus there meanwhile).
 */
function focusLost() {
  const active = document.activeElement
  return !active || active === document.body || Boolean(active.closest('[hidden], [inert]'))
}

/**
 * The phone's panels under the video (docs/features/watch-experience.md): Cameras, Map and Chat as a Play `Tabs` in
 * its inverse tone (the prototype's dark switcher: an ink pill, the open tab yellow), icons with their names as
 * tooltips, floating at the foot of the screen. Every panel stays mounted while hidden, so a tab switch never reloads
 * a tile's video or the map. Chat counts unread messages while another tab is open, as Play's corner badge.
 *
 * The Map tab can fill the screen (`fullMap`): a full-screen toggle at its foot, level with the switcher (Maximize,
 * then Minimize in the same place), which the page answers by hiding everything but the map and a small player of the
 * video. The toggle, the player or Escape go back.
 *
 * On its side the upright switcher and its panels hide (`hidden`, still mounted, so the videos and the map stay in the
 * document) and the same inverse switcher, smaller and top right, flips the screen between Video, Cameras, Map and Chat
 * with the upright glyphs (the video's camera for the video). The camera grid, the map and the chat move into its
 * panels (the Cameras panel's foot stays behind, hidden); the Video panel is empty, the canvas behind it being the
 * video. Escape from the cameras, the map or the chat goes back to the video, focus on its tab. Over the video, the
 * page's stage button (an event with other stages) sits beside the switcher. Turning the phone with focus in what the
 * turn hides puts it on the shown switcher's open tab.
 */
export const PhoneTabs = memo(function PhoneTabs({
  tab,
  onTabChange,
  unread,
  cameras,
  map,
  chat,
  footer = null,
  landscape = false,
  view = 'video',
  onViewChange,
  fullMap = false,
  onFullMapChange,
  stageButton = null,
}: PhoneTabsProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const viewsRef = useRef<HTMLDivElement>(null)
  const switchRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const fullScreen = landscape ? view !== 'video' : fullMap

  // Escape from the full map, or from the cameras, map or chat on its side, goes back to the video: a window listener
  // in the bubble phase, so whatever used the key first (a zoomed face, the chat's menus) has marked it, and nothing
  // with a popup open (a menu, a dialog) loses its own Escape.
  const back = useRef(() => {})
  useLayoutEffect(() => {
    back.current = () => {
      if (landscape) {
        onViewChange?.('video')
        switchRef.current?.querySelector<HTMLElement>('[role="tab"]')?.focus({ preventScroll: true })
      } else {
        onFullMapChange?.(false)
        toggleRef.current?.focus({ preventScroll: true })
      }
    }
  })
  useEffect(() => {
    if (!fullScreen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return
      if (document.activeElement?.closest(POPUP)) return
      event.preventDefault()
      back.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [fullScreen])

  // Back to the video some other way (the small player, which goes with it; a camera picked in the grid): focus left
  // behind lands on the control that went there, the full-screen toggle or the switcher's tab, rather than on the page.
  const wasFullScreen = useRef(fullScreen)
  useLayoutEffect(() => {
    const was = wasFullScreen.current
    wasFullScreen.current = fullScreen
    if (!was || fullScreen || !focusLost()) return
    const target = landscape ? selectedTab(switchRef.current) : toggleRef.current
    target?.focus({ preventScroll: true })
  }, [fullScreen, landscape])

  // Turning the phone hides one switcher and its panels, and moves what they hold: focus that was in them and is now
  // somewhere hidden (a tile in a view not on screen, the switcher that went) lands on the shown switcher's open tab.
  // Focus that was never in them (the page, the site's header) stays where it is: the native focus events say, as
  // the camera grid and the chat render through portals, and a removed element sends none.
  const focusWithin = useRef(false)
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const onFocusIn = () => {
      focusWithin.current = true
    }
    const onFocusOut = (event: FocusEvent) => {
      focusWithin.current = root.contains(event.relatedTarget as Node | null)
    }
    root.addEventListener('focusin', onFocusIn)
    root.addEventListener('focusout', onFocusOut)
    return () => {
      root.removeEventListener('focusin', onFocusIn)
      root.removeEventListener('focusout', onFocusOut)
    }
  }, [])
  const wasLandscape = useRef(landscape)
  useLayoutEffect(() => {
    const turned = wasLandscape.current !== landscape
    wasLandscape.current = landscape
    if (!turned || !focusWithin.current || !focusLost()) return
    selectedTab(landscape ? switchRef.current : viewsRef.current)?.focus({ preventScroll: true })
  }, [landscape])

  const tabUnread = tab === 'chat' ? 0 : unread
  const viewUnread = view === 'chat' ? 0 : unread
  const fullMapToggle =
    onFullMapChange ? (
      <IconButton
        ref={toggleRef}
        variant="secondary"
        elevated
        className="watch-phone__full-map"
        label={fullMap ? 'Exit full-screen map' : 'Full-screen map'}
        onClick={() => onFullMapChange(!fullMap)}
      >
        {fullMap ? <Minimize2 /> : <Maximize2 />}
      </IconButton>
    ) : null
  const items: TabItem<WatchTab>[] = [
    {
      value: 'cameras',
      label: 'Cameras',
      icon: <LayoutGrid />,
      keepMounted: true,
      // On its side the grid is in that switcher's panel instead; the foot stays here, hidden.
      content: (
        <div className="watch-phone__panel" data-panel="cameras">
          {landscape ? null : cameras}
          {footer}
        </div>
      ),
    },
    {
      value: 'map',
      label: 'Map',
      icon: <MapIcon />,
      keepMounted: true,
      // On its side the map is in that switcher's panel instead.
      content: landscape ? null : (
        <div className="watch-phone__panel" data-panel="map">
          {map}
          {fullMapToggle}
        </div>
      ),
    },
    {
      value: 'chat',
      label: 'Chat',
      icon: <MessageCircle />,
      // An icon tab's count is a corner badge (99+, none at zero); the name and tooltip read "Chat, 3 new".
      count: tabUnread,
      countLabel: `${tabUnread} new`,
      keepMounted: true,
      content: landscape ? null : <div className="watch-phone__panel" data-panel="chat">{chat}</div>,
    },
  ]
  const views: TabItem<LandscapeView>[] = [
    // The canvas behind the switcher is the video: its panel is empty (watch.css takes it out of the way).
    { value: 'video', label: 'Video', icon: <Video />, keepMounted: true, content: null },
    {
      value: 'cameras',
      label: 'Cameras',
      icon: <LayoutGrid />,
      keepMounted: true,
      content: <div className="watch-phone__layer" data-panel="cameras">{cameras}</div>,
    },
    {
      value: 'map',
      label: 'Map',
      icon: <MapIcon />,
      keepMounted: true,
      content: <div className="watch-phone__layer" data-panel="map">{map}</div>,
    },
    {
      value: 'chat',
      label: 'Chat',
      icon: <MessageCircle />,
      count: viewUnread,
      countLabel: `${viewUnread} new`,
      keepMounted: true,
      content: <div className="watch-phone__layer" data-panel="chat">{chat}</div>,
    },
  ]

  return (
    <div
      ref={rootRef}
      className="watch-phone"
      data-landscape={landscape || undefined}
      data-view={landscape ? view : undefined}
      data-full-map={(!landscape && fullMap) || undefined}
    >
      <div ref={viewsRef} className="watch-phone__views" hidden={landscape || undefined}>
        <Tabs label="View" items={items} value={tab} onValueChange={onTabChange} iconOnly tone="inverse" className="watch-phone__tabs" />
      </div>
      {landscape && view === 'video' && stageButton && <div className="watch-phone__stage">{stageButton}</div>}
      {landscape && (
        <Tabs
          ref={switchRef}
          label="View"
          items={views}
          value={view}
          onValueChange={(next) => onViewChange?.(next)}
          iconOnly
          tone="inverse"
          className="watch-phone__switch"
        />
      )}
    </div>
  )
})
